// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  Signal,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { EmojiPickerComponent } from '@fc-shared/ui/emoji-picker/emoji-picker.component';
import { DialogHeaderComponent } from '@fc-shared/ui/dialog-opener/dialog-header.component';
import { DialogContentComponent } from '@fc-shared/ui/dialog-opener/dialog-content.component';
import { FormFieldComponent } from '@fc-shared/ui/input/core/form-field/form-field.component';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { DialogFooterComponent } from '@fc-shared/ui/dialog-opener/dialog-footer.component';
import { ErrorComponent } from '@fc-core/form-utils/form-error/error.component';
import { PrefixDirective } from '@fc-shared/ui/input/core/prefix.directive';
import { InputDirective } from '@fc-shared/ui/input/core/input.directive';
import { EntityType } from '@fc-shared/ui/status-set-dialog';
import { Emoji } from '@fc-shared/ui/emoji-picker/emoji.model';

// Re-exported for backwards compatibility — the canonical type now lives in
// the emoji-picker package (breaks the dialog <-> picker circular import).
export type { Emoji };

export interface StatusSetDialogData {
  id: string | number;
  entityType: EntityType;
  status: string;
  icon: string;
  loading: Signal<boolean>;
  error: Signal<string>;
  updateFunction: Function;
  clearFunction?: Function;
}

@Component({
  selector: 'fc-status-set-dialog',
  imports: [
    DialogHeaderComponent,
    FormFieldComponent,
    NgButtonComponent,
    PrefixDirective,
    ReactiveFormsModule,
    InputDirective,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    EmojiPickerComponent,
    ErrorComponent,
    DialogContentComponent,
    DialogFooterComponent,
  ],
  template: `
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="isOpen"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="['cdk-overlay-transparent-backdrop']"
      (backdropClick)="isOpen = false"
    >
      <fc-emoji-picker (onEmojiSelect)="selectEmoji($event)"></fc-emoji-picker>
    </ng-template>
    <fc-dialog-header title="Set a status"></fc-dialog-header>
    <fc-dialog-content>
      <fc-form-field>
        <ng-button
          variant="icon"
          (click)="isOpen = !isOpen"
          cdkOverlayOrigin
          #trigger="cdkOverlayOrigin"
          fcPrefix
          iconPosition="end"
          [icon]="selectedEmoji ? null : 'icon-emoji'"
        >
          @if (selectedEmoji) {
            {{ selectedEmoji }}
          }
        </ng-button>
        <input
          [formControl]="statusTextControl"
          fcInput
          maxlength="64"
          placeholder="What\`s your {{ dialogData.data.entityType }} status"
        />
        @if (
          (statusTextControl.invalid && statusTextControl.touched) ||
          dialogData.data.error()
        ) {
          <fc-error>{{ statusInputError }}</fc-error>
        }
      </fc-form-field>
    </fc-dialog-content>
    <fc-dialog-footer
      primaryText="Save"
      secondaryText="Clear Status"
      (secondaryClicked)="clearStatus()"
      (primaryClicked)="saveStatus()"
      [loading]="dialogData.data.loading()"
      [disabled]="statusTextControl.value.trim().length === 0"
    ></fc-dialog-footer>
  `,
  styles: `
    @use 'var' as *;

    :host {
      min-width: 836px;
      display: flex;
      flex-direction: column;

      @media (max-width: $sm-size) {
        min-width: 100%;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusSetDialogComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  dialogData = inject<{ data: StatusSetDialogData }>(MAT_DIALOG_DATA);

  statusTextControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(64),
  ]);
  isOpen: boolean = false;
  selectedEmoji: string | null = null;

  ngOnInit() {
    if (this.dialogData.data.status) {
      this.statusTextControl.setValue(this.dialogData.data.status);
      this.selectedEmoji = this.dialogData.data.icon;
    }
  }

  get statusInputError(): string {
    if (this.statusTextControl.hasError('required')) {
      return 'Status is required';
    }
    return this.dialogData.data.error();
  }

  clearStatus() {
    this.dialogData.data.clearFunction(this.dialogData.data.id);
  }

  saveStatus() {
    if (!this.selectedEmoji) {
      this.selectedEmoji = '💬';
    }
    this.dialogData.data.updateFunction(
      this.dialogData.data.id,
      this.statusTextControl.value,
      this.selectedEmoji,
    );
  }

  selectEmoji(emoji: Emoji): void {
    this.cdr.markForCheck();
    this.selectedEmoji = emoji.native;
  }
}
